import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { clientService } from '../../../services/clientService';

const ClientPhotoGallery = ({ siteId, siteName }) => {
  const [photos, setPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    if (siteId) loadPhotos();
  }, [siteId, dateFrom, dateTo]);

  const loadPhotos = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await clientService?.getSitePhotos(siteId, { dateFrom, dateTo });
      if (err) throw err;
      setPhotos(data || []);
      // Load signed URLs for first 20 photos
      loadPhotoUrls(data?.slice(0, 20) || []);
    } catch (err) {
      console.error('[ClientPhotoGallery] load photos failed:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301') {
        setError(`Permission denied loading photos: ${err?.message}`);
      } else {
        setError(`Failed to load photos: ${err?.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPhotoUrls = async (photoList) => {
    const urlMap = {};
    await Promise.all(
      photoList?.map(async (photo) => {
        if (photo?.file_path) {
          const { url, error: err } = await clientService?.getPhotoUrl(photo?.file_path);
          if (err) {
            console.error('[ClientPhotoGallery] thumbnail signed URL failed:', { id: photo?.id, code: err?.code, message: err?.message });
            return;
          }
          if (url) urlMap[photo?.id] = url;
        }
      })
    );
    setPhotoUrls(prev => ({ ...prev, ...urlMap }));
  };

  const handlePhotoClick = async (photo) => {
    setSelectedPhoto(photo);
    if (photoUrls?.[photo?.id]) {
      setSelectedUrl(photoUrls?.[photo?.id]);
    } else if (photo?.file_path) {
      const { url, error: err } = await clientService?.getPhotoUrl(photo?.file_path);
      if (err) {
        console.error('[ClientPhotoGallery] lightbox signed URL failed:', { id: photo?.id, code: err?.code, message: err?.message });
      }
      setSelectedUrl(url || null);
    }
  };

  const hasFilters = dateFrom || dateTo;

  return (
    <div className="bg-card rounded-xl border border-border">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="Images" size={18} className="text-accent" />
          <h3 className="font-semibold text-foreground">Site Photos</h3>
          {photos?.length > 0 && (
            <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{photos?.length}</span>
          )}
        </div>
      </div>

      {/* Date filters */}
      <div className="px-5 py-3 border-b border-border bg-muted/20 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground font-medium">From:</label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e?.target?.value)}
            className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground font-medium">To:</label>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e?.target?.value)}
            className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        {hasFilters && (
          <button
            onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Clear
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8]?.map(i => (
              <div key={i} className="aspect-square rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-10">
            <Icon name="AlertCircle" size={24} className="text-red-400 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        ) : photos?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Images" size={32} className="text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm font-medium text-foreground mb-1">No photos found</p>
            <p className="text-xs text-muted-foreground">
              {hasFilters ? 'Try adjusting the date range.' : 'No photos have been uploaded for this site yet.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {photos?.map(photo => (
              <button
                key={photo?.id}
                onClick={() => handlePhotoClick(photo)}
                className="group relative aspect-square rounded-xl overflow-hidden bg-muted border border-border hover:border-accent/50 transition-all"
              >
                {photoUrls?.[photo?.id] ? (
                  <img
                    src={photoUrls?.[photo?.id]}
                    alt={photo?.task?.title || 'Site photo'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="Image" size={24} className="text-muted-foreground/40" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                {photo?.task?.title && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-xs font-medium truncate">{photo?.task?.title}</p>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => { setSelectedPhoto(null); setSelectedUrl(null); }}
        >
          <div className="relative max-w-4xl w-full" onClick={e => e?.stopPropagation()}>
            <button
              onClick={() => { setSelectedPhoto(null); setSelectedUrl(null); }}
              className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
            {selectedUrl ? (
              <img
                src={selectedUrl}
                alt={selectedPhoto?.task?.title || 'Site photo'}
                className="w-full max-h-[80vh] object-contain rounded-xl"
              />
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
              </div>
            )}
            <div className="mt-3 text-center">
              {selectedPhoto?.task?.title && (
                <p className="text-white font-medium">{selectedPhoto?.task?.title}</p>
              )}
              <p className="text-white/60 text-sm mt-1">
                {selectedPhoto?.uploader?.full_name && `Uploaded by ${selectedPhoto?.uploader?.full_name} · `}
                {new Date(selectedPhoto?.created_at)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPhotoGallery;
